import PropTypes from 'prop-types';

import { useState, createContext, useEffect, useContext } from 'react';
// Firebase auth functions
import { signInWithEmailAndPassword, signOut as firebaseSignOut, createUserWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
// import Auth from '../auth/Auth';

// Firebase auth instance
import { db, auth as firebaseAuth } from '../services/firebase';
import { collection, getDocs, query, where, setDoc, doc } from 'firebase/firestore';
import { login } from 'services/apiService';
import useToken from 'services/useToken';

export const AuthContext = createContext({
  isSignedIn: false,
  user: undefined,
  loading: false,
  signInAPI: false,
});

export const ContextProvider = ({ children }) => {
  // States to check auth status
  const {token, setToken, removeToken} = useToken();
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [user, setUser] = useState();
  const [loading, setLoading] = useState(true);

  // Function to check and set auth state
  const checkAuthState = () => {
    // Check for partner session FIRST (sessionStorage) - takes precedence over regular token
    // Partner sessions are temporary and should override any existing localStorage token
    const partnerToken = sessionStorage.getItem('auth_token');
    const isPartnerSession = sessionStorage.getItem('is_partner_session');
    
    if (partnerToken && isPartnerSession === 'true') {
      const partnerUser = JSON.parse(sessionStorage.getItem('user') || '{}');
      setUser(partnerUser);
      setIsSignedIn(true);
      setLoading(false);
      return;
    }
    
    // Check for regular user token (localStorage)
    if (token) {
      const userData = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('appUser') || '{}');
      setUser(userData);
      setIsSignedIn(true);
      setLoading(false);
      return;
    }
    
    // No token found
    setIsSignedIn(false);
    setUser(undefined);
    setLoading(false);
  };

  // Check auth state on mount and when token changes
  useEffect(() => {
    checkAuthState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Also check sessionStorage on mount and when page becomes visible (for partner sessions)
  useEffect(() => {
    // Check immediately on mount
    checkAuthState();
    
    // Listen for page visibility changes (in case sessionStorage is set in another tab/window)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAuthState();
      }
    };
    
    // Listen for storage events (for cross-tab communication)
    const handleStorageChange = (e) => {
      if (e.key === 'auth_token' || e.key === 'is_partner_session' || e.key === 'user') {
        checkAuthState();
      }
    };
    
    // Set up a short polling interval for the first few seconds to catch async writes
    const interval = setInterval(() => {
      checkAuthState();
    }, 200);
    
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 3000);
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Run once on mount

  /* useEffect(() => {
    // Listener updates auth status when detects change
    onAuthStateChanged(firebaseAuth, (user) => {
      if (user) {
        console.log('user', user);
        // user.getIdToken().then((token) => {
        //   //   Auth.login(token);
        // });
        setIsSignedIn(true);
        setUser(user);
      } else {
        setIsSignedIn(false);
        setUser(undefined);
      }
      setLoading(false);
    });
  }, []); */

  // Functions handling auth
  const signInApi = async (data) => {
    try {
      setLoading(true);
      const response = await login(data);
      setLoading(false);

  
      if (response && response.data.user_data && response.data.token) {
        setToken(response.data.token);
        setIsSignedIn(true);
        setUser(response.data.user_data);
        localStorage.setItem('appUser', JSON.stringify(response.data.user_data));
      } else {
        setIsSignedIn(false);
      }
      return response;
    } catch (error) {
      setLoading(false);
      console.error('SignIn Error:', error.message);
      throw new Error(error.message || 'Invalid user credentials or user does not belong to this organization');
    }
  };
  
  const signIn = async (email, password) => {
    let getResponse;
    setLoading(true);
    await signInWithEmailAndPassword(firebaseAuth, email, password).then(
      async function (response) {
        getResponse = response;
      },
      (error) => {
        getResponse = error;
      }
    );
    setLoading(false);
    return getResponse;
  };
  const signUp = async (email, password, firstName, lastName) => {
    let getResponse;
    await createUserWithEmailAndPassword(firebaseAuth, email, password).then(
      async function (response) {
        getResponse = response;
        //save user to user's table
        const user = response.user;
        const userEmail = user?.email;
        const usersRef = collection(db, `users`);
        const querySnapshot = await getDocs(query(usersRef, where('email', '==', userEmail)));
        if (querySnapshot.empty) {
          console.info('User not found in DB. creating new user', user);
          await setDoc(doc(db, `users/${userEmail}`), {
            id: user.email,
            email: user.email,
            firstName: user.firstName || firstName,
            lastName: user.lastName || lastName,
            user_uid: user.uid,
            user_type: 'USER',
            devices: []
          });
        } else {
          console.info('User found in DB');
        }
      },
      (error) => {
        getResponse = error;
      }
    );
    return getResponse;
  };
  /* const signOut = async () => {
    try {
      await firebaseSignOut(firebaseAuth);
    } catch (err) {
      if (err instanceof Error) {
        // console.log(err.message);
        return err.message;
      }
    }
  }; */
  const signOut = () => {
    removeToken();
    setIsSignedIn(false);
    setUser(undefined);
    localStorage.removeItem('org_name');
    localStorage.removeItem('appUser');
  };
  // Context provider
  return (
    <AuthContext.Provider
      value={{
        isSignedIn,
        user,
        signIn,
        signInApi,
        signUp,
        signOut,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthContext.propTypes = {
  children: PropTypes.object
};

export const useAuth = () => useContext(AuthContext);
